import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import type { UrlRedirect, UrlRedirectHttpCode, UrlRedirectPayload } from '../types/urlRedirect';

const HTTP_CODES: UrlRedirectHttpCode[] = [301, 302, 307, 308];

const emptyForm = (): UrlRedirectPayload => ({
  source_path: '',
  target_url: '/',
  http_code: 301,
  is_active: true,
  note: '',
});

const UrlRedirectsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<UrlRedirect | null>(null);
  const [form, setForm] = React.useState<UrlRedirectPayload>(emptyForm());
  const [formError, setFormError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<UrlRedirect | null>(null);

  const { data: redirects = [], isLoading, error } = useQuery({
    queryKey: ['url-redirects'],
    queryFn: () => apiService.getUrlRedirectsAdmin(),
    select: (res) => res.redirects ?? [],
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: UrlRedirectPayload = {
        source_path: form.source_path.trim(),
        target_url: form.target_url.trim(),
        http_code: form.http_code,
        is_active: form.is_active,
        note: form.note?.trim() ? form.note.trim() : null,
      };
      if (editing) {
        return apiService.updateUrlRedirectAdmin(editing.id, payload);
      }
      return apiService.createUrlRedirectAdmin(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['url-redirects'] });
      closeDialog();
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Erreur lors de l’enregistrement';
      setFormError(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteUrlRedirectAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['url-redirects'] });
      setDeleteTarget(null);
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: UrlRedirect) => {
    setEditing(row);
    setForm({
      source_path: row.source_path,
      target_url: row.target_url,
      http_code: row.http_code,
      is_active: row.is_active,
      note: row.note ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setFormError(null);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Redirections d’URLs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Gérez les anciennes URLs indexées par Google (ancien site) vers les nouvelles pages.
            Les redirections actives sont compilées en règles Apache sur le site public (sans PHP
            à chaque visite). Elles s’appliquent au prochain déploiement front, ou immédiatement si
            la sync OVH est configurée côté API (
            <code>URL_REDIRECTS_FRONT_SYNC_URL</code> / <code>URL_REDIRECTS_FRONT_SYNC_KEY</code>
            ).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Ajouter une redirection
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Exemple : <strong>/ancienne-page</strong> → <strong>/</strong> ou <strong>/announcements</strong>.
        Utilisez un chemin interne (commençant par /) ou une URL complète https://…
      </Alert>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Impossible de charger les redirections.</Alert>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ancienne URL</TableCell>
                    <TableCell>Destination</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {redirects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Aucune redirection configurée.
                      </TableCell>
                    </TableRow>
                  ) : (
                    redirects.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{row.source_path}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', maxWidth: 280 }}>{row.target_url}</TableCell>
                        <TableCell>{row.http_code}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.is_active ? 'Active' : 'Inactive'}
                            color={row.is_active ? 'success' : 'default'}
                            variant={row.is_active ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary" noWrap title={row.note ?? ''}>
                            {row.note || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(row)} aria-label="Modifier">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)} aria-label="Supprimer">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Modifier la redirection' : 'Nouvelle redirection'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError ? <Alert severity="error">{formError}</Alert> : null}
            <TextField
              label="Ancienne URL (chemin source)"
              placeholder="/ancienne-page"
              value={form.source_path}
              onChange={(e) => setForm((f) => ({ ...f, source_path: e.target.value }))}
              fullWidth
              required
              helperText="Chemin exact de l’ancien site, sans le domaine"
            />
            <TextField
              label="Destination"
              placeholder="/ ou /announcements"
              value={form.target_url}
              onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Code HTTP</InputLabel>
              <Select
                label="Code HTTP"
                value={form.http_code}
                onChange={(e) => setForm((f) => ({ ...f, http_code: e.target.value as UrlRedirectHttpCode }))}
              >
                {HTTP_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code} {code === 301 ? '(permanent, recommandé SEO)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Note interne (optionnel)"
              value={form.note ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Redirection active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !form.source_path.trim() || !form.target_url.trim()}
          >
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer la redirection</DialogTitle>
        <DialogContent>
          <Typography>
            Supprimer la redirection de <strong>{deleteTarget?.source_path}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UrlRedirectsPage;
